import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CalendarMonthGrid, { getMonthGridRange } from './CalendarMonthGrid';
import CalendarWeekGrid, { getWeekGridRange } from './CalendarWeekGrid';
import { toISODate } from './calendarConstants';
import { todayAsLocalDate, dateWordsLocale, formatDateWords } from '../../shared/tenantClock';

const toggleBtnStyle = (active) => ({
  border: '1px solid #b3bbc7',
  background: active ? '#1976d2' : '#fff',
  color: active ? '#fff' : '#333',
  fontSize: 13,
  fontWeight: 600,
  padding: '5px 12px',
  cursor: 'pointer',
});

const navBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  border: '1px solid #b3bbc7',
  background: '#fff',
  borderRadius: 4,
  cursor: 'pointer',
  color: '#5a6b85',
};

// Home Dashboard Calendar (issue #229) — shows scheduled Meetings, Calls,
// and Tasks in a month, fortnight or week grid, replacing the old mock
// "Upcoming Deadlines" section. Trade Shows with a date are shown too, as a
// quick fix: the Events and Platform Calendar visions replace them.
// Fortnight shows this week and next as two rows, so a Task can be dragged
// across into the following week.
const WEEKS_IN_VIEW = { week: 1, fortnight: 2 };

function DashboardCalendar({ initialView }) {
  const [viewMode, setViewMode] = useState(initialView || 'fortnight');
  const [anchorDate, setAnchorDate] = useState(() => todayAsLocalDate());
  const [items, setItems] = useState([]);
  const [tradeShows, setTradeShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  const { from, to } = useMemo(() => {
    if (viewMode === 'month') {
      const { gridStart, gridEnd } = getMonthGridRange(anchorDate);
      return { from: toISODate(gridStart), to: toISODate(gridEnd) };
    }
    const { weekStart, weekEnd } = getWeekGridRange(anchorDate, WEEKS_IN_VIEW[viewMode]);
    return { from: toISODate(weekStart), to: toISODate(weekEnd) };
  }, [viewMode, anchorDate]);

  useEffect(() => {
    // Clicking Next quickly fires several loads; only the latest one counts.
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get('/api/activities/calendar', { params: { from, to } }).then((res) => res.data.data || []).catch(() => []),
      axios.get('/api/trade-shows', { params: { from, to } }).then((res) => res.data.data || []).catch(() => []),
    ]).then(([activities, shows]) => {
      if (cancelled) return;
      setItems(activities);
      setTradeShows(shows);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [from, to]);

  // Trade Shows go first on each day they run (start to end date, or the
  // start date alone), ahead of that day's Activities.
  const itemsByDate = useMemo(() => {
    const map = {};
    const add = (date, item) => { (map[date] = map[date] || []).push(item); };
    tradeShows.forEach((show) => {
      if (!show.start_date) return;
      const name = `${show.series?.name || 'Trade Show'} ${show.year || ''}`.trim();
      const end = show.end_date && show.end_date > show.start_date ? show.end_date : show.start_date;
      const dateRange = end === show.start_date
        ? formatDateWords(show.start_date)
        : `${formatDateWords(show.start_date)} – ${formatDateWords(end)}`;
      const d = new Date(`${show.start_date}T00:00:00`);
      for (let iso = show.start_date; iso <= end && iso <= to; d.setDate(d.getDate() + 1), iso = toISODate(d)) {
        if (iso >= from) add(iso, { kind: 'trade_show', id: `ts-${show.id}-${iso}`, name, dateRange });
      }
    });
    items.forEach((item) => {
      if (item.activity_date) add(item.activity_date, item);
    });
    return map;
  }, [items, tradeShows, from, to]);

  // Drag a Task chip to a different day to change its Due Date — same
  // optimistic-update-then-revert-on-failure shape as CustomerPipelinePage's
  // moveEntity, backed by activityController.rescheduleTask.
  const handleTaskDrop = (newDate) => {
    setDragOverDate(null);
    const taskId = draggingTaskId;
    setDraggingTaskId(null);
    const task = items.find((i) => i.id === taskId);
    if (!task || task.activity_date === newDate) return;

    const previousDate = task.activity_date;
    setItems((prev) => prev.map((i) => (i.id === taskId ? { ...i, activity_date: newDate } : i)));
    axios.patch(`/api/activities/${taskId}/date`, { activity_date: newDate })
      .catch(() => setItems((prev) => prev.map((i) => (i.id === taskId ? { ...i, activity_date: previousDate } : i))));
  };

  const dragProps = {
    draggingTaskId,
    dragOverDate,
    setDragOverDate,
    onDragStartTask: setDraggingTaskId,
    onDragEndTask: () => { setDraggingTaskId(null); setDragOverDate(null); },
    onDropTask: handleTaskDrop,
  };

  const goToday = () => setAnchorDate(todayAsLocalDate());
  // Week and Fortnight both step a week at a time, so Fortnight always
  // keeps the week you were on in view alongside the next one.
  const goPrev = () => setAnchorDate(viewMode === 'month'
    ? new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 1, 1)
    : new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate() - 7));
  const goNext = () => setAnchorDate(viewMode === 'month'
    ? new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 1)
    : new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate() + 7));

  const periodLabel = viewMode === 'month'
    ? anchorDate.toLocaleDateString(dateWordsLocale(), { month: 'long', year: 'numeric' })
    : (() => {
      const { weekStart, weekEnd } = getWeekGridRange(anchorDate, WEEKS_IN_VIEW[viewMode]);
      const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
      const startLabel = weekStart.toLocaleDateString(dateWordsLocale(), { day: 'numeric', month: sameMonth ? undefined : 'short' });
      const endLabel = weekEnd.toLocaleDateString(dateWordsLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
      return `${startLabel} – ${endLabel}`;
    })();

  return (
    <div style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" style={navBtnStyle} onClick={goPrev} aria-label="Previous"><ChevronLeft size={16} /></button>
          <button type="button" style={navBtnStyle} onClick={goNext} aria-label="Next"><ChevronRight size={16} /></button>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#222', marginLeft: 4 }}>{periodLabel}</span>
          <button
            type="button"
            onClick={goToday}
            style={{ border: '1px solid #b3bbc7', background: '#fff', borderRadius: 4, fontSize: 12, fontWeight: 600, color: '#5a6b85', padding: '4px 10px', cursor: 'pointer', marginLeft: 4 }}
          >
            Today
          </button>
        </div>
        <div style={{ display: 'flex' }}>
          <button type="button" style={{ ...toggleBtnStyle(viewMode === 'month'), borderRadius: '4px 0 0 4px' }} onClick={() => setViewMode('month')}>Month</button>
          <button type="button" style={{ ...toggleBtnStyle(viewMode === 'fortnight'), borderRadius: 0, borderLeft: 'none' }} onClick={() => setViewMode('fortnight')}>Fortnight</button>
          <button type="button" style={{ ...toggleBtnStyle(viewMode === 'week'), borderRadius: '0 4px 4px 0', borderLeft: 'none' }} onClick={() => setViewMode('week')}>Week</button>
        </div>
      </div>

      {/* The grid stays rendered while the next period loads (dimmed, not
          swapped for a short "Loading..." line), so the page height holds
          steady and the browser's scrollbar doesn't flick off and on,
          shifting the whole page sideways. */}
      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 120ms' }} aria-busy={loading}>
        {viewMode === 'month' ? (
          <CalendarMonthGrid anchorDate={anchorDate} itemsByDate={itemsByDate} dragProps={dragProps} />
        ) : (
          <CalendarWeekGrid anchorDate={anchorDate} itemsByDate={itemsByDate} dragProps={dragProps} weeks={WEEKS_IN_VIEW[viewMode]} />
        )}
      </div>
    </div>
  );
}

export default DashboardCalendar;
